import { useTutorials } from "@/api/dashboard-home";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  ALL_CATEGORIES,
  countTutorials,
  filterTutorials,
} from "@/lib/dashboard-home/tutorial-search";
import { openTutorials } from "@/lib/open-tutorials";
import { cn } from "@/lib/utils";
import { ArrowRight, Play } from "lucide-react";
import { useMemo, useState } from "react";
import { HomeDialogHeader } from "../HomeDialogHeader";
import { HomeSearchInput } from "../HomeSearchInput";
import { AcademicCapIcon } from "../HomeIcons";

/** Liste des tutoriels vidéo (page WordPress lue par l'API), avec recherche et rubriques. */
export const TutorialsDialog = ({
  open,
  onOpenChange,
  focusSearch,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ouverte depuis « Rechercher un tutoriel » : le champ prend le focus. */
  focusSearch: boolean;
}) => {
  const vt = useVisibleT();
  const { data: categories, isLoading, isError } = useTutorials(open);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(ALL_CATEGORIES);

  const filtered = useMemo(
    () => filterTutorials(categories ?? [], category, query),
    [categories, category, query],
  );
  const count = countTutorials(filtered);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setQuery("");
      setCategory(ALL_CATEGORIES);
    }
    onOpenChange(next);
  };

  const chipClass = (active: boolean) =>
    cn(
      "rounded-full border px-3 py-1 text-xs transition-colors",
      active
        ? "border-primary bg-brand-50 text-primary"
        : "border-slate-200 text-slate-900 hover:bg-slate-100",
    );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        needCloseButton={false}
        className="flex max-h-[90vh] max-w-2xl flex-col gap-0 rounded-xl p-6 text-slate-900 sm:rounded-xl"
        onOpenAutoFocus={(e) => {
          if (!focusSearch) e.preventDefault();
        }}
      >
        <HomeDialogHeader
          icon={<AcademicCapIcon />}
          title={vt("bo.home.tutorials.title", "Mes tutoriels")}
          description={vt(
            "bo.home.tutorials.subtitle",
            "Retrouvez les guides pour prendre en main InventoZen.",
          )}
        />

        <HomeSearchInput
          value={query}
          onChange={setQuery}
          placeholder={vt("bo.home.tutorials.search", "Rechercher un tutoriel")}
          className="mt-4"
        />

        {(categories?.length ?? 0) > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              type="button"
              className={chipClass(category === ALL_CATEGORIES)}
              onClick={() => setCategory(ALL_CATEGORIES)}
            >
              {vt("bo.home.tutorials.all", "Tous")}
            </button>
            {categories?.map((c) => (
              <button
                key={c.title}
                type="button"
                className={chipClass(category === c.title)}
                onClick={() => setCategory(c.title)}
              >
                {c.title}
              </button>
            ))}
          </div>
        )}

        <div className="-mx-1 mt-4 min-h-[120px] flex-1 overflow-y-auto px-1">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 rounded-md" />
              ))}
            </div>
          ) : isError || (categories?.length ?? 0) === 0 ? (
            // Liste illisible (WordPress injoignable…) : la page reste accessible.
            <div className="flex flex-col items-start gap-3 text-sm text-slate-500">
              {vt(
                "bo.home.tutorials.unavailable",
                "La liste des tutoriels n’est pas disponible pour le moment.",
              )}
              <Button type="button" onClick={() => void openTutorials()}>
                {vt(
                  "bo.home.tutorials.openPage",
                  "Ouvrir la page des tutoriels",
                )}
              </Button>
            </div>
          ) : count === 0 ? (
            <p className="text-sm text-slate-500">
              {vt(
                "bo.home.tutorials.noResult",
                "Aucun tutoriel ne correspond à votre recherche.",
              )}
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              {filtered.map((c) => (
                <section key={c.title} className="flex flex-col gap-2">
                  <h3 className="text-sm font-medium text-slate-500">
                    {c.title}
                  </h3>
                  {c.items.map((item) => (
                    <a
                      key={item.url}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 rounded-md border border-slate-200 p-2.5 transition-colors hover:border-primary"
                    >
                      <span className="flex h-8 w-12 shrink-0 items-center justify-center rounded-md bg-brand-50">
                        <Play
                          className="size-3.5 fill-primary text-primary"
                          aria-hidden
                        />
                      </span>
                      <span className="min-w-0 flex-1 text-sm font-medium">
                        {item.title}
                      </span>
                      <ArrowRight
                        className="size-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                        aria-hidden
                      />
                    </a>
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>

        <footer className="mt-5 flex items-center justify-between gap-3">
          <span className="text-sm text-slate-500">
            {count}{" "}
            {count > 1
              ? vt("bo.home.tutorials.countPlural", "tutoriels")
              : vt("bo.home.tutorials.countSingular", "tutoriel")}
          </span>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            {vt("bo.home.close", "Fermer")}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
